// src/lib/dueAdapt.ts — 213B §22 [RULED 50] (addendum 6): A COLLECTIVE CASE'S ROUND DUE, ADAPTED FOR ONE MEMBER,
// WITH ITS NOTE.
//
// The engine already takes an adapted due: a member's `dueByRound` {round number → amount} IS the due where it has
// an entry (server/src/lib/retainerRounds.js `dueFor`; ruled 27/08, the override wins), and the cell is flagged
// `stated` (164 §A.7). Until 213B only the Excel import wrote it — « the fee rounds columns can ONLY be calculated »
// (Laurent, 02/10). The round's payment window now writes it too, with a note saying why (`dueNotes`, the one new
// column, beside `dueByRound` on the junction), and « Revenir à la formule » removes both.
//
// Everything here is pure and takes its rows as `unknown`: CollectiveCaseView holds its links and its compute inputs
// as `any`, and a typed callback over them costs the file's ESLint count, which may never rise (213 addendum 3
// [METHOD] (b) — the seam that keeps the level). Recorded by recordings/213b-due-notes.
//
// THE FORMULA'S AMOUNT for a round whose due is adapted is NOT computed here (never a second computation of money —
// 213B [METHOD] (a)): the window asks the engine once more, for the same member WITHOUT that round's override
// (`membersWithoutOverride`), and reads the answer's cell (`dueInResult`).
import { clearImportMark } from "./ccClientImport";

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? (v as unknown[]) : []);
/** `m` without the key `k` (no dynamic delete). */
const without = (m: Rec, k: string): Rec => Object.fromEntries(Object.entries(m).filter(([key]) => key !== k));
/** `out` with `key` set to `m` when `m` has keys, else without `key`. */
const withMap = (out: Rec, key: string, m: Rec): Rec => (Object.keys(m).length ? { ...out, [key]: m } : without(out, key));

/** The member's adapted due for `roundNumber`, when it has one (a finite number), else null. */
export const adaptedDueOf = (link: unknown, roundNumber: number): number | null => {
  const v = rec(rec(link).dueByRound)[String(roundNumber)];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
};

/** The member's note for `roundNumber` — why its due was adapted — else "". */
export const dueNoteOf = (link: unknown, roundNumber: number): string => {
  const v = rec(rec(link).dueNotes)[String(roundNumber)];
  return typeof v === "string" ? v : "";
};

/** The compute's members with ONE member's override for ONE round removed: the engine then answers the formula's
 *  amount for that cell (and the termination gate's, where it applies — the engine's own rule). Every other member
 *  and every other key posts exactly as before. */
export const membersWithoutOverride = (members: unknown, clId: number, roundNumber: number): unknown[] =>
  arr(members).map((m) => {
    const r = rec(m);
    if (r.clId !== clId) return m;
    return withMap(r, "dueByRound", without(rec(r.dueByRound), String(roundNumber)));
  });

/** One cell's due, read off a compute result (`perMember[].perRound[]`), else null. */
export const dueInResult = (result: unknown, clId: number, roundNumber: number): number | null => {
  const pm = arr(rec(result).perMember).find((x) => rec(x).clId === clId);
  const cell = arr(rec(pm).perRound).find((x) => rec(x).roundNumber === roundNumber);
  const due = rec(cell).due;
  return typeof due === "number" && Number.isFinite(due) ? due : null;
};

/** The link with this round's due ADAPTED to `amount` (cent-rounded, as the server rounds every due) and its note
 *  set — or removed when the note is blank; the import's mark on the cell cleared (207 §4: a value set in the app
 *  clears the mark). */
export const withAdaptedDue = (link: unknown, roundNumber: number, amount: number, note: string): Rec => {
  const r = rec(link);
  const k = String(roundNumber);
  const due: Rec = { ...rec(r.dueByRound), [k]: Math.round(amount * 100) / 100 };
  const text = note.trim();
  const notes: Rec = text ? { ...rec(r.dueNotes), [k]: text } : without(rec(r.dueNotes), k);
  const out = withMap({ ...r, dueByRound: due }, "dueNotes", notes);
  return clearImportMark(out as { importMarks?: Record<string, string> }, "due:" + k);
};

/** The link BACK ON THE FORMULA for this round: the adapted amount and its note removed together (ruling 50 —
 *  « Revenir à la formule » removes both); the import's mark on the cell cleared. */
export const withFormulaDue = (link: unknown, roundNumber: number): Rec => {
  const r = rec(link);
  const k = String(roundNumber);
  const out = withMap(withMap(r, "dueByRound", without(rec(r.dueByRound), k)), "dueNotes", without(rec(r.dueNotes), k));
  return clearImportMark(out as { importMarks?: Record<string, string> }, "due:" + k);
};

/** `links` with the one member's link (by `clId`) adapted for `roundNumber`; every other element as it was. */
export const linksWithAdaptedDue = (links: unknown, clId: number, roundNumber: number, amount: number, note: string): unknown[] =>
  arr(links).map((x) => (rec(x).clId === clId ? withAdaptedDue(x, roundNumber, amount, note) : x));

/** `links` with the one member's link (by `clId`) back on the formula for `roundNumber`; every other element as it was. */
export const linksWithFormulaDue = (links: unknown, clId: number, roundNumber: number): unknown[] =>
  arr(links).map((x) => (rec(x).clId === clId ? withFormulaDue(x, roundNumber) : x));

/** An adapted amount typed in the window: "" is nothing typed; a finite number of zero or more is an amount (a
 *  round waived in full is 0); anything else is refused. Cent-rounded. */
export const parseAdaptedAmount = (text: string): number | null => {
  if (text.trim() === "") return null;
  const n = Number(text.replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100) / 100;
};
